import { PROJECT_STATUS_FROM_API, PROJECT_STATUS_TO_API } from "@/features/projects/config";
import {
  CreateProjectSchema,
  DeleteProjectSchema,
  InviteUserSchema,
  LeaveProjectSchema,
  RemoveMemberSchema,
  RevokeInvitationSchema,
  UpdateMemberSchema,
  UpdateProjectSchema,
  createProjectSchema,
  deleteProjectSchema,
  inviteUserSchema,
  leaveProjectSchema,
  removeMemberSchema,
  revokeInvitationSchema,
  updateMemberSchema,
  updateProjectSchema,
} from "@/features/projects/schemas";
import {
  Project,
  ProjectInvitation,
  ProjectInvitationsPage,
  ProjectListItem,
  ProjectMember,
  ProjectMembersPage,
  ProjectRoleType,
  ProjectStatus,
  ProjectSummary,
  ProjectSummaryActivity,
  ProjectSummaryActivityType,
  ProjectSummaryMemberActivity,
  ProjectSummaryPriorityCount,
  ProjectSummaryStatusCount,
  ProjectSummaryTrendPoint,
  ProjectTag,
  ProjectTagsPage,
  ProjectVisibility,
  ProjectsPage,
} from "@/features/projects/types";
import { GetMyProjectsProjectDto } from "@/features/projects/types/dtos";
import { CreateProjectRequest, InviteUserRequest, UpdateMemberRequest, UpdateProjectRequest } from "@/features/projects/types/requests";
import {
  CreateProjectResponse,
  GetMyPermissionsInProjectResponse,
  GetMyProjectsResponse,
  GetProjectByIdResponse,
  GetProjectInvitationsResponse,
  GetProjectMembersResponse,
  GetProjectSummaryResponse,
  GetProjectTagsResponse,
  InviteUserResponse,
  UpdateMemberResponse,
  UpdateProjectResponse,
} from "@/features/projects/types/responses";
import { STATUS_FROM_API } from "@/features/tickets/config";
import { Priority, Status } from "@/features/tickets/types";
import { badRequest } from "@/shared/lib/utils/errors";
import http from "@/shared/lib/utils/http";
import { toErrorDetails } from "@/shared/lib/utils/zod";

function toProject(dto: GetProjectByIdResponse): Project {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    status: PROJECT_STATUS_FROM_API[dto.status] ?? (dto.status as ProjectStatus),
    visibility: dto.visibility as ProjectVisibility,
    createdAt: new Date(dto.created_at),
    createdBy: {
      id: dto.created_by.id,
      firstName: dto.created_by.first_name,
      lastName: dto.created_by.last_name,
      emailAddress: dto.created_by.email_address,
    },
    totalMemberCount: dto.total_member_count,
    pendingInvitationsCount: dto.pending_invitations_count,
    openTicketCount: dto.open_ticket_count,
    totalTicketCount: dto.total_ticket_count,
    role: { name: dto.role.name as ProjectRoleType },
  };
}

function toProjectListItem(dto: GetMyProjectsProjectDto): ProjectListItem {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    status: PROJECT_STATUS_FROM_API[dto.status] ?? (dto.status as ProjectStatus),
    createdAt: new Date(dto.created_at),
    totalMemberCount: dto.total_member_count,
    openTicketCount: dto.open_ticket_count,
    role: { name: dto.role.name as ProjectRoleType },
  };
}

function toProjectSummary(dto: GetProjectSummaryResponse): ProjectSummary {
  return {
    stats: {
      openTicketCount: dto.stats.open_ticket_count,
      openedThisWeekCount: dto.stats.opened_this_week_count,
      inProgressTicketCount: dto.stats.in_progress_ticket_count,
      inProgressUnassignedCount: dto.stats.in_progress_unassigned_count,
      resolvedTicketCount: dto.stats.resolved_ticket_count,
      resolvedThisMonthCount: dto.stats.resolved_this_month_count,
      memberCount: dto.stats.member_count,
      adminMemberCount: dto.stats.admin_member_count,
    },
    ticketTrend: dto.ticket_trend.map(
      (point): ProjectSummaryTrendPoint => ({
        year: point.year,
        month: point.month,
        createdCount: point.created_count,
        resolvedCount: point.resolved_count,
      })
    ),
    statusBreakdown: dto.status_breakdown.map(
      (entry): ProjectSummaryStatusCount => ({
        status: STATUS_FROM_API[entry.status] ?? (entry.status as Status),
        count: entry.count,
      })
    ),
    priorityBreakdown: dto.priority_breakdown.map(
      (entry): ProjectSummaryPriorityCount => ({
        priority: entry.priority as Priority,
        count: entry.count,
      })
    ),
    memberActivity: dto.member_activity.map(
      (entry): ProjectSummaryMemberActivity => ({
        memberId: entry.member_id,
        firstName: entry.first_name ?? null,
        lastName: entry.last_name ?? null,
        closedCount: entry.closed_count,
      })
    ),
    recentActivity: dto.recent_activity.map(
      (entry): ProjectSummaryActivity => ({
        type: entry.type as ProjectSummaryActivityType,
        ticketId: entry.ticket_id,
        ticketKey: entry.ticket_key,
        ticketTitle: entry.ticket_title,
        status: entry.status ? (STATUS_FROM_API[entry.status] ?? (entry.status as Status)) : null,
        firstName: entry.first_name ?? null,
        lastName: entry.last_name ?? null,
        occurredAt: new Date(entry.occurred_at),
      })
    ),
  };
}

export async function createProject(values: CreateProjectSchema): Promise<string> {
  const validationResult = createProjectSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Create Project", toErrorDetails(validationResult.error));
  }

  const request: CreateProjectRequest = {
    name: values.name.trim(),
    description: values.description?.trim(),
  };

  const response = await http.post<CreateProjectResponse>({
    url: "/projects",
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function getMyProjects(startingAfter?: number | null): Promise<ProjectsPage> {
  const params = new URLSearchParams();

  if (startingAfter != null) {
    params.set("starting_after", String(startingAfter));
  }

  const response = await http.get<GetMyProjectsResponse>({
    url: `/users/me/projects?${params.toString()}`,
    contentType: "none",
  });

  const projects = response.projects.map(toProjectListItem);

  return {
    projects,
    hasMore: response.has_more,
    nextStartingAfter: response.next_starting_after ?? null,
  };
}

export async function getProjectById(id: string): Promise<Project> {
  const response = await http.get<GetProjectByIdResponse>({
    url: `/projects/${id}`,
    contentType: "none",
  });

  return toProject(response);
}

export async function getProjectSummary(id: string): Promise<ProjectSummary> {
  const response = await http.get<GetProjectSummaryResponse>({
    url: `/projects/${id}/summary`,
    contentType: "none",
  });

  return toProjectSummary(response);
}

export async function updateProject(values: UpdateProjectSchema): Promise<string> {
  const validationResult = updateProjectSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Update Project", toErrorDetails(validationResult.error));
  }

  const request: UpdateProjectRequest = {
    name: values.name,
    description: values.description,
    status: PROJECT_STATUS_TO_API[values.status],
  };

  const response = await http.put<UpdateProjectResponse>({
    url: `/projects/${values.id}`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function deleteProject(values: DeleteProjectSchema): Promise<undefined> {
  const validationResult = deleteProjectSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Delete Project", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: `/projects/${values.id}`,
    contentType: "none",
  });
}

export async function inviteUser(values: InviteUserSchema): Promise<string> {
  const validationResult = inviteUserSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Invite User", toErrorDetails(validationResult.error));
  }

  const request: InviteUserRequest = {
    email_address: values.emailAddress,
    id: values.roleId,
  };

  const response = await http.post<InviteUserResponse>({
    url: `/projects/${values.projectId}/invitations`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function revokeInvitation(values: RevokeInvitationSchema): Promise<undefined> {
  const validationResult = revokeInvitationSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Revoke Invitation", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: `/projects/${values.projectId}/invitations/${values.invitationId}`,
    contentType: "none",
  });
}

export async function leaveProject(values: LeaveProjectSchema): Promise<undefined> {
  const validationResult = leaveProjectSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Leave Project", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: `/projects/${values.id}/members/me`,
    contentType: "none",
  });
}

export async function getProjectMembers(id: string, startingAfter?: number | null): Promise<ProjectMembersPage> {
  const params = new URLSearchParams();

  if (startingAfter != null) {
    params.set("starting_after", String(startingAfter));
  }

  const response = await http.get<GetProjectMembersResponse>({
    url: `/projects/${id}/members?${params.toString()}`,
    contentType: "none",
  });

  const members = response.members.map(
    (dto): ProjectMember => ({
      id: dto.id,
      userId: dto.user_id,
      firstName: dto.first_name,
      lastName: dto.last_name,
      emailAddress: dto.email_address,
      joinedAt: new Date(dto.joined_at),
      openTicketCount: dto.open_ticket_count,
      closedTicketCount: dto.closed_ticket_count,
      totalTicketCount: dto.total_ticket_count,
      role: { name: dto.role.name as ProjectRoleType },
    })
  );

  return {
    members,
    hasMore: response.has_more,
    nextStartingAfter: response.next_starting_after ?? null,
  };
}

export async function getProjectInvitations(id: string, startingAfter?: number | null): Promise<ProjectInvitationsPage> {
  const params = new URLSearchParams();

  if (startingAfter != null) {
    params.set("starting_after", String(startingAfter));
  }

  const response = await http.get<GetProjectInvitationsResponse>({
    url: `/projects/${id}/invitations?${params.toString()}`,
    contentType: "none",
  });

  const invitations = response.invitations.map(
    (dto): ProjectInvitation => ({
      id: dto.id,
      emailAddress: dto.email_address,
      role: dto.role,
      invitedAt: new Date(dto.invited_at),
      expiresAt: new Date(dto.expires_at),
      isExpired: dto.is_expired,
      invitedBy: {
        id: dto.invited_by.id,
        firstName: dto.invited_by.first_name,
        lastName: dto.invited_by.last_name,
        emailAddress: dto.invited_by.email_address,
      },
    })
  );

  return {
    invitations,
    hasMore: response.has_more,
    nextStartingAfter: response.next_starting_after ?? null,
  };
}

export async function getMyPermissionsInProject(id: string): Promise<string[]> {
  const response = await http.get<GetMyPermissionsInProjectResponse>({
    url: `/projects/${id}/members/me/permissions`,
    contentType: "none",
  });

  return response.permissions;
}

export async function updateProjectMember(values: UpdateMemberSchema): Promise<string> {
  const validationResult = updateMemberSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Update Member", toErrorDetails(validationResult.error));
  }

  const request: UpdateMemberRequest = {
    id: values.roleId,
  };

  const response = await http.put<UpdateMemberResponse>({
    url: `/projects/${values.projectId}/members/${values.memberId}`,
    body: JSON.stringify(request),
    contentType: "application/json",
  });

  return response.id;
}

export async function removeProjectMember(values: RemoveMemberSchema): Promise<undefined> {
  const validationResult = removeMemberSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Remove Member", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: `/projects/${values.projectId}/members/${values.memberId}`,
    contentType: "none",
  });
}

export async function getProjectTags(id: string, startingAfter?: number | null): Promise<ProjectTagsPage> {
  const params = new URLSearchParams();

  if (startingAfter != null) {
    params.set("starting_after", String(startingAfter));
  }

  const response = await http.get<GetProjectTagsResponse>({
    url: `/projects/${id}/tags?${params.toString()}`,
    contentType: "none",
  });

  const tags = response.tags.map(
    (dto): ProjectTag => ({
      id: dto.id,
      name: dto.name,
      createdAt: new Date(dto.created_at),
    })
  );

  return {
    tags,
    hasMore: response.has_more,
    nextStartingAfter: response.next_starting_after ?? null,
  };
}
